import { defineStore } from "pinia";
import { getDoctorsInfo } from "@/api/getInfo";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("doctorsList");

// 使用更规范的命名：Doctor 而非 doctorsType
export interface Doctor {
  avatar: string;
  badge: string;
  category: string;
  experience: string;
  expertiseList: Array<{ expertise: string; sortOrder: number }>;
  id: number;
  major: boolean;
  name: string;
  specialty: string;
  title: string;
  type: number;
}

type CategoryType = {
  id: number;
  label: string;
  value: string;
};

// 状态接口命名：DoctorsState 而非 doctorsListType
interface DoctorsState {
  doctors: Doctor[];
}

// 职称排序权重映射（比数组 indexOf 更高效）
const TITLE_WEIGHT: Record<string, number> = {
  主任医师: 1,
  主任检验师: 2,
  副主任医师: 3,
  副主任检验师: 4,
  主治医师: 5,
  主管检验师: 6,
  执业医师: 7,
  检验师: 8,
};

const DEFAULT_TITLE_WEIGHT = 999;

// 获取职称排序权重
const getTitleWeight = (title: string): number =>
  TITLE_WEIGHT[title] ?? DEFAULT_TITLE_WEIGHT;

// 排序比较函数
const compareByTitle = (a: Doctor, b: Doctor): number =>
  getTitleWeight(a.title) - getTitleWeight(b.title);

// 过滤并排序（先过滤后排序，减少排序数据量）
const filterAndSort = (
  doctors: Doctor[],
  predicate: (d: Doctor) => boolean,
): Doctor[] => doctors.filter(predicate).sort(compareByTitle);
// 科室名称映射配置
const SPECIALTY_MAP: Record<string, { label: string; value: string }> = {
  急诊内科: { label: "门急诊", value: "emergency" },
  急诊外科: { label: "门急诊", value: "emergency" },
};

// 获取规范化后的科室信息
const getNormalizedCategory = (
  doctor: Doctor,
): { label: string; value: string } => {
  const mapped = SPECIALTY_MAP[doctor.specialty];
  return mapped || { label: doctor.specialty, value: doctor.category };
};
export const useDoctorsListStore = defineStore("doctorsList", {
  state: (): DoctorsState => ({
    doctors: [],
  }),

  getters: {
    allDoctors: (state): Doctor[] => state.doctors,

    sortedDoctors: (state): Doctor[] => [...state.doctors].sort(compareByTitle),

    homeOutDoctors: (state): Doctor[] =>
      filterAndSort(state.doctors, (d) => d.type === 1 && d.major),

    homeInDoctors: (state): Doctor[] =>
      filterAndSort(state.doctors, (d) => d.type === 2 && d.major),

    majorDoctors: (state): Doctor[] =>
      filterAndSort(state.doctors, (d) => d.major),

    doctorsByCategory:
      (state) =>
      (category: string): Doctor[] =>
        filterAndSort(state.doctors, (d) => d.category === category),

    doctorsCategories: (state): CategoryType[] => {
      // 使用 Map 去重,保持插入顺序
      const categoryMap = new Map<string, CategoryType>([
        ["all", { id: 0, label: "全院", value: "all" }],
      ]);


      state.doctors.forEach((doctor) => {
        // 获取规范化后的科室信息(不修改原数据)
        let { label, value } = getNormalizedCategory(doctor);
        // 和并 value=medical 和 value=laboratory 的医生到一个分类
        if (value === "medical" || value === "laboratory") {
          value = "medical";
          label = "医技科室";
        }


        // 如果该分类不存在,则添加
        if (!categoryMap.has(value)) {
          categoryMap.set(value, {
            id: categoryMap.size, // 使用递增序号作为 ID
            label,
            value,
          });
        }
      });

      return Array.from(categoryMap.values());
    },
  },

  actions: {
    async fetchDoctorsList(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.doctors.length > 0 && !forceRefresh) {
        return;
      }

      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<Doctor[]>(PERSISTENCE_CONFIG);

        if (cachedData && this.isValidDoctorArray(cachedData)) {
          this.doctors = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const response = await getDoctorsInfo();
        const data = response?.data;

        if (!this.isValidDoctorArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.doctors = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<Doctor[]>(PERSISTENCE_CONFIG);
          if (fallbackData && this.isValidDoctorArray(fallbackData)) {
            this.doctors = fallbackData;
            return;
          }
        }

        if (this.doctors.length === 0) {
          throw error;
        }
      }
    },

    async refreshInBackground(): Promise<void> {
      try {
        const response = await getDoctorsInfo();
        const data = response?.data;

        if (this.isValidDoctorArray(data)) {
          this.doctors = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    async refreshDoctors(): Promise<void> {
      return this.fetchDoctorsList(true, false);
    },

    clearCache(): void {
      this.doctors = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },

    isValidDoctorArray(data: unknown): data is Doctor[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            typeof (item as Doctor).id === "number",
        )
      );
    },

    updateDoctor(updatedDoctor: Doctor): void {
      const index = this.doctors.findIndex((d) => d.id === updatedDoctor.id);
      if (index !== -1) {
        this.doctors[index] = { ...this.doctors[index], ...updatedDoctor };
        writeCache(PERSISTENCE_CONFIG, this.doctors);
      }
    },
  },
});
